import { clsx } from 'clsx';
import React from 'react';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  CloseIcon,
  CompressIcon,
  ExpandIcon,
} from './Icons';
import styles from './PaneActions.module.scss';

/** Localisable tooltips for `<PaneActions>`. */
export interface PaneActionsLocales {
  /** Tooltip on the move-up button. */
  moveUp?: string;
  /** Tooltip on the move-down button. */
  moveDown?: string;
  /** Tooltip on the collapse / expand button. */
  collapse?: string;
  /** Tooltip on the maximize / restore button. */
  maximize?: string;
  /** Tooltip on the remove (×) button. */
  remove?: string;
}

/**
 * Props for `<PaneActions>` — the icon group revealed on hover at the
 * top-right of every pane.
 *
 * Required: `paneId`, `isFirst`, `isLast`, `isPrimary`, `isCollapsed`,
 * `isMaximized`, `canRemove`. Each callback is optional and the matching
 * button is hidden when its handler is omitted.
 */
export interface PaneActionsProps {
  /** Stable pane identifier (forwarded to handlers). */
  paneId: string;
  /** True when this is the topmost pane (hides move-up). */
  isFirst: boolean;
  /** True when this is the bottommost pane (hides move-down). */
  isLast: boolean;
  /** True for the primary pane — hides remove regardless of `canRemove`. */
  isPrimary: boolean;
  /** Current collapsed state (controls the icon variant). */
  isCollapsed: boolean;
  /** Current maximized state (controls the icon variant). */
  isMaximized: boolean;
  /** Whether the remove (×) button should be rendered. */
  canRemove: boolean;
  /** Called when the move-up button is clicked. */
  onMoveUp?: () => void;
  /** Called when the move-down button is clicked. */
  onMoveDown?: () => void;
  /** Called when the collapse / expand button is clicked. */
  onCollapse?: () => void;
  /** Called when the maximize / restore button is clicked. */
  onMaximize?: () => void;
  /** Called when the remove (×) button is clicked. */
  onRemove?: () => void;
  /** Tooltip overrides — see `PaneActionsLocales`. */
  locales?: PaneActionsLocales;
  /** Additional class name applied to the actions container. */
  className?: string;
}

interface ActionConfig {
  id: string;
  visible: boolean;
  active?: boolean;
  label?: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

/**
 * Hover-revealed icon group at the top-right of every chart pane.
 * Exposes move-up / move-down / collapse / maximize / remove buttons
 * (each conditionally rendered based on its handler being supplied
 * and the pane's position in the stack).
 *
 * When `isMaximized` is true, only the maximize button is shown
 * (so the user can restore). Move buttons are auto-hidden on the
 * first / last pane; remove is hidden on the primary pane.
 */
export const PaneActions = ({
  paneId,
  isFirst,
  isLast,
  isPrimary,
  isCollapsed,
  isMaximized,
  canRemove,
  onMoveUp,
  onMoveDown,
  onCollapse,
  onMaximize,
  onRemove,
  locales,
  className,
}: PaneActionsProps) => {
  if (isFirst && isLast && !isCollapsed) {
    return null;
  }

  const maximizeAction: ActionConfig = {
    id: 'maximize',
    visible: !!onMaximize,
    active: isMaximized,
    label: locales?.maximize ?? 'Maximize',
    icon: <ExpandIcon />,
    onClick: onMaximize,
  };

  const actions: ActionConfig[] = isMaximized
    ? [maximizeAction]
    : [
        {
          id: 'move-up',
          visible: !isFirst && !!onMoveUp,
          label: locales?.moveUp ?? 'Move up',
          icon: <ArrowUpIcon />,
          onClick: onMoveUp,
        },
        {
          id: 'move-down',
          visible: !isLast && !!onMoveDown,
          label: locales?.moveDown ?? 'Move down',
          icon: <ArrowDownIcon />,
          onClick: onMoveDown,
        },
        {
          id: 'collapse',
          visible: !!onCollapse,
          active: isCollapsed,
          label: locales?.collapse ?? 'Collapse',
          icon: <CompressIcon />,
          onClick: onCollapse,
        },
        maximizeAction,
        {
          id: 'remove',
          visible: !isPrimary && canRemove && !!onRemove,
          label: locales?.remove ?? 'Remove',
          icon: <CloseIcon />,
          onClick: onRemove,
        },
      ];

  const visibleActions = actions.filter(a => a.visible);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(styles.root, className)}
      data-testid={`chartv2-pane-actions-${paneId}`}
    >
      {visibleActions.map(action => (
        <button
          key={action.id}
          type="button"
          className={clsx(styles.button, action.active && styles.active)}
          onClick={action.onClick}
          aria-label={action.label}
          title={action.label}
          data-testid={`chartv2-pane-action-${action.id}-${paneId}`}
        >
          {action.icon}
        </button>
      ))}
    </div>
  );
};
